/* eslint-disable max-lines */
import styled from '@emotion/styled';
import { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import P1 from '@base/P1/P1';
import H4 from '@base/H4/H4';
import H6 from '@base/H6/H6';
import Dialog from '@comp/Dialog/Dialog';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import pushRoute from '@utils/pushRoute';
import { pluralize } from '@utils/textHelper';
import { removeEntityFromSaved } from '@actions/saved';
import EduItemCard from '@educator/Container/CourseDetails/ItemCard';
import { isPost } from '@models/Item';
import useGoalEventProps from '@hooks/useGoalEventProps';
import useAllTypesOfCourseEventProps from '@hooks/useAllTypesOfCourseEventProps';
import { useSnackbar } from '@stores/snackbar';
import { segment } from '@actions/analytics';
import {
  BATCH_SCHEDULE,
  BATCH_KEYS,
  SAVE_UNSAVE_CONTENT,
  PLATFORM_BATCH_SCHEDULE
} from '@constants/segment';
import { getSavedContentType } from '@utils/segment/propsHelper';
import { Spacings, mediaQuery, Fonts } from '@styles/index';
import { savedObjectTypes, evtNameCategoryMap } from '@constants/index';
import useEducatorContext from '@hooks/useEducatorContext';
import { getMe } from '@selectors/auth';
import EarlyMorning from '@icon/EarlyMorning';
import Morning from '@icon/Morning';
import Afternoon from '@icon/Afternoon';
import Evening from '@icon/Evening';
import LateEvening from '@icon/LateEvening';
import Clock from '@icon/Clock';
import PlayRectangle from '@icon/PlayRectangle';
import usePlatformEventProps from '@hooks/usePlatformBatchEventProps';
import useWindowSize from '@hooks/useWindowSize';
import H5 from '@base/H5/H5';
import MockTest from '@icon/MockTest';
import CalenderClock from '@icon/CalenderClock';
import ItemCard from './ItemCard';

const classTimeIconMap = {
  1: EarlyMorning,
  2: Morning,
  3: Afternoon,
  4: Evening,
  5: LateEvening,
  6: Clock,
  7: Clock,
  20: Clock
};

const WeekInfo = styled(H6)`
  margin-top: ${Spacings.SPACING_6};
  color: var(--color-text-primary);
  ${mediaQuery.uptoMobile} {
    color: var(--color-text-secondary);
  }
`;

const CountInfo = styled(P1)`
  color: var(--color-text-secondary);
`;

const CountWrapper = styled.div`
  margin-top: ${Spacings.SPACING_2B};
`;

const WeekInfoWrapper = styled.div`
  grid-column: 2/9;
  position: sticky;
  top: 235px;
  opacity: ${({ isEducatorSide, notAccessible }) =>
    isEducatorSide ? 1 : notAccessible && 0.5};
  ${mediaQuery.uptoMobile} {
    top: 48px;
    background-color: var(--color-base-0);
    width: 100%;
    padding-top: ${Spacings.SPACING_4B};
    padding-bottom: ${({ $isStats }) =>
      $isStats ? Spacings.SPACING_0B : Spacings.SPACING_4B};
    z-index: 1;
    opacity: 1;
    grid-column: span 10;
    margin-bottom: 8px;
  }
`;

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(24, 1fr);
  grid-column-gap: ${Spacings.SPACING_4B};
  grid-column: span 24;
  margin-top: ${Spacings.SPACING_10B};
  position: relative;
  margin-bottom: ${({ isBatch }) => (isBatch ? 0 : Spacings.SPACING_13B)};
  ${mediaQuery.uptoMobile} {
    display: block;
    margin-bottom: 0px;
    grid-column: span 10;
    grid-template-columns: repeat(10, 1fr);
    margin-top: ${Spacings.SPACING_6B};
  }
`;

const SeeAll = styled(H6)`
  grid-column: 9/25;
  background-color: var(--color-base-1);
  height: 70px;
  text-align: center;
  cursor: pointer;
  padding: ${Spacings.SPACING_6B};
  border-radius: ${Spacings.SPACING_2B};
  box-shadow: ${boxShadowGenerator()};
  color: var(--color-text-primary);
  ${mediaQuery.uptoMobile} {
    grid-column: span 10;
    height: 56px;
    padding: 18px 0;
    font-size: 14px;
  }
`;

const Description = styled.div`
  color: var(--color-text-secondary);
  margin-bottom: ${Spacings.SPACING_2B};
  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_6B};
  }
`;

const InfoRow = styled.div`
  display: flex;
  margin-top: ${Spacings.SPACING_2B};
  align-items: center;
`;

const IconWrap = styled.div`
  margin-right: ${Spacings.SPACING_2B};
  width: ${Spacings.SPACING_4B};
  height: ${Spacings.SPACING_4B};
`;

const StyledP1 = styled(P1)`
  color: var(--color-text-secondary);
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.P2};
    line-height: ${Spacings.SPACING_5B};
  }
`;

const Week = ({
  goalUID,
  lessonList,
  onItemClick,
  weekTitle,
  weekInfo,
  weekPlusInfoData,
  showEducatorForLesson,
  isTestSeries,
  isSubscribed,
  forPlus,
  cardPosition,
  subscription,
  setAnchorEl,
  showActionMenu,
  setLessonDrawer,
  isBatch,
  batch,
  renewalData,
  showMoreMenu,
  lastPrimarySourceSection,
  isBatchGroup,
  showLiveBatch,
  courseType,
  accessEndDate,
  hasDppAccess,
  isOnlyRecordedClassesPresent
}) => {
  const { isMobile } = useWindowSize();
  const me = useSelector(getMe);
  let retreivedGoalUID = goalUID || batch?.goal?.uid;
  if (!retreivedGoalUID)
    retreivedGoalUID = lessonList.length ? lessonList[0].goalUid : null;
  const goalSegmentData = useGoalEventProps({ goalUID: retreivedGoalUID });
  const segmentData = useAllTypesOfCourseEventProps({
    course: batch,
    isBatch: true
  });
  const platformsSegmentData = usePlatformEventProps({
    batch,
    isBatch: true,
    lastPrimarySourceSection,
    lastPrimarySource: lastPrimarySourceSection
  });
  const {
    timeType,
    lessonsCount = 0,
    quizzesCount = 0,
    testSeries = 0,
    classFrequencyType = '',
    mockTestFrequencyType = ''
  } = batch || {};
  const typeOfClassTime = timeType?.type;
  const ClassTimeType = typeOfClassTime && classTimeIconMap[typeOfClassTime];
  const isEducatorSide = useEducatorContext();
  const { open: showSnackbar } = useSnackbar();

  const handleClick = () => {
    if (isBatchGroup) {
      segment.track(PLATFORM_BATCH_SCHEDULE, {
        ...segmentData,
        ...platformsSegmentData
      });
    }
    if (isBatch) {
      segment.track(
        BATCH_SCHEDULE,
        {
          ...goalSegmentData,
          ...segmentData,
          batch_state: batch.state,
          is_store_exclusive: batch?.isStoreExclusive
        },
        evtNameCategoryMap.WITHOUT_CATEGORY,
        true,
        BATCH_KEYS
      );
    }

    if (isBatchGroup) {
      pushRoute(
        `/batch/[slug]/[batchUID]/schedule?associated_batch=${batch.batchUID}`,
        `/batch/${batch.slug}/${batch.uid}/schedule?associated_batch=${batch.batchUID}`
      );
    } else {
      pushRoute(
        '/batch/[slug]/[batchUID]/schedule',
        `/batch/${batch.slug}/${batch.uid}/schedule`
      );
    }
  };

  const [isUnEnrollModalOpen, setIsUnEnrollModalOpen] = useState(false);
  const selectedLesson = useRef();
  const showRemoveDialog = (lesson) => {
    selectedLesson.current = lesson;
    setIsUnEnrollModalOpen(true);
  };
  const onCloseModal = () => {
    setIsUnEnrollModalOpen(false);
    selectedLesson.current = null;
  };
  const dispatch = useDispatch();
  const removeFromCollection = () => {
    const lesson = selectedLesson.current;
    if (lesson) {
      const goalUid = lesson.goal?.uid || lesson.goalUid;
      const isLesson = isPost(lesson);
      const entityType = isLesson
        ? savedObjectTypes.LESSON
        : savedObjectTypes.TEST;
      dispatch(
        removeEntityFromSaved({
          goalUid,
          objectUid: lesson.uid,
          entityType,
          key: `${goalUid}-${isLesson ? 'lessons' : 'tests'}`
        })
      ).then(() => {
        showSnackbar({
          message: `${isLesson ? 'Lesson' : 'Test'} removed successfully`,
          type: 'success'
        });
        segment.track(SAVE_UNSAVE_CONTENT, {
          save_content_type: getSavedContentType(entityType),
          save_content_uid: lesson.uid,
          save_content_title: lesson.name,
          ...goalSegmentData
        });
      });
    }
    onCloseModal();
  };

  const getLiveClassText = () => {
    let str = '';
    const testCount = quizzesCount + testSeries;
    const testText = testCount ? pluralize(testCount, 'test') : '';
    if (lessonsCount) str += `${lessonsCount}+ live classes`;
    if (str && testText) str += ' • ';
    str += testText;
    if (str) {
      return (
        <InfoRow>
          <IconWrap>
            <PlayRectangle
              size="16px"
              aria-hidden="true"
              color="var(--color-text-secondary)"
            />
          </IconWrap>
          <StyledP1>{str}</StyledP1>
        </InfoRow>
      );
    }
  };

  const getMockTestText = () => {
    return mockTestFrequencyType ? (
      <InfoRow>
        <IconWrap>
          <MockTest
            size="16px"
            aria-hidden="true"
            color="var(--color-text-secondary)"
          />
        </IconWrap>
        <StyledP1>{`Mock tests • ${classFrequencyType}`}</StyledP1>
      </InfoRow>
    ) : null;
  };

  const getAccessText = () => {
    return accessEndDate ? (
      <InfoRow>
        <IconWrap>
          <CalenderClock
            size="16px"
            aria-hidden="true"
            color="var(--color-text-secondary)"
          />
        </IconWrap>
        <StyledP1>{`Access ends on ${accessEndDate}`}</StyledP1>
      </InfoRow>
    ) : null;
  };

  const getScheduleStats = () => (
    <Description>
      {typeOfClassTime && (
        <InfoRow>
          <IconWrap>
            <ClassTimeType
              size="16px"
              aria-hidden="true"
              color="var(--color-text-secondary)"
            />
          </IconWrap>
          <StyledP1>{`${timeType.text} ${
            classFrequencyType ? `• ${classFrequencyType}` : ''
          }`}</StyledP1>
        </InfoRow>
      )}
      {getMockTestText()}
      {getAccessText()}
      {!isBatch && getLiveClassText()}
    </Description>
  );

  const renderWeekInfo = () => {
    if (weekPlusInfoData && forPlus) {
      const { dateInfo, lessonCountInfo, dppCount } = weekPlusInfoData;
      return (
        <>
          <H5>{dateInfo}</H5>
          <CountWrapper>
            <CountInfo>{lessonCountInfo}</CountInfo>
            {dppCount > 0 ? (
              <CountInfo>{pluralize(dppCount, 'practice')}</CountInfo>
            ) : null}
          </CountWrapper>
        </>
      );
    }
    if (weekInfo) {
      return (
        <WeekInfo color="var(--color-text-secondary)" as="h5">
          {weekInfo}
        </WeekInfo>
      );
    }
  };

  return (
    <Wrapper isBatch={isBatch}>
      <WeekInfoWrapper
        id={weekTitle}
        notAccessible={
          cardPosition !== -1 && cardPosition <= lessonList[0].rank
        }
        isEducatorSide={isEducatorSide}
        $isStats={typeOfClassTime}
      >
        <H4 color="var(--color-text-primary)">{weekTitle}</H4>
        {renderWeekInfo()}
        {!isMobile && getScheduleStats()}
      </WeekInfoWrapper>
      {isMobile && getScheduleStats()}
      <Dialog
        onClose={onCloseModal}
        dialogState={isUnEnrollModalOpen}
        heading="Remove from Saved?"
        message={`This will remove the ${
          isPost(selectedLesson.current) ? 'lesson' : 'test'
        } from your saved list. You can add it back from the ${
          isPost(selectedLesson.current) ? 'course' : 'test series'
        } page.`}
        messageWidth="82.5%"
        acceptLabel="Remove"
        rejectLabel="Cancel"
        acceptOnClick={removeFromCollection}
        rejectOnClick={onCloseModal}
      />
      {/*  */}
      {lessonList.map((lesson) => {
        if (!isEducatorSide) {
          return (
            <ItemCard
              key={lesson.uid}
              lesson={lesson}
              onItemClick={onItemClick}
              showEducatorForLesson={showEducatorForLesson}
              isTestSeries={isTestSeries}
              isSubscribed={isSubscribed}
              forPlus={forPlus}
              courseType={courseType}
              batchType={batch?.batchType}
              cardPosition={cardPosition}
              subscription={subscription}
              setPdfAnchorEl={setAnchorEl}
              setAnchorEl={showActionMenu}
              isBatch={isBatch}
              showMoreMenu={showMoreMenu}
              renewalData={renewalData}
              showRemoveDialog={showRemoveDialog}
              goalUID={goalUID}
              isBatchGroup={isBatchGroup}
              showLiveBatch={showLiveBatch}
              hasDppAccess={hasDppAccess}
              isOnlyRecordedClassesPresent={isOnlyRecordedClassesPresent}
            />
          );
        }
        return (
          <EduItemCard
            courseType={courseType}
            key={lesson.uid}
            lesson={lesson}
            onItemClick={onItemClick}
            isTestSeries={isTestSeries}
            setAnchorEl={setAnchorEl}
            setLessonDrawer={setLessonDrawer}
            me={me}
          />
        );
      })}

      {isBatch && <SeeAll onClick={handleClick}>View complete schedule</SeeAll>}
    </Wrapper>
  );
};

export default Week;

const doNothing = () => {};

Week.propTypes = {
  lessonList: PropTypes.arrayOf(PropTypes.any).isRequired,
  onItemClick: PropTypes.func.isRequired,
  weekTitle: PropTypes.string.isRequired,
  weekInfo: PropTypes.string.isRequired,
  showEducatorForLesson: PropTypes.bool,
  isTestSeries: PropTypes.bool,
  isSubscribed: PropTypes.bool,
  forPlus: PropTypes.bool,
  cardPosition: PropTypes.number,
  setAnchorEl: PropTypes.func,
  showActionMenu: PropTypes.func,
  setLessonDrawer: PropTypes.func,
  isBatch: PropTypes.bool,
  batch: PropTypes.objectOf(PropTypes.any),
  subscription: PropTypes.objectOf(PropTypes.any),
  renewalData: PropTypes.objectOf(PropTypes.any),
  showMoreMenu: PropTypes.bool,
  goalUID: PropTypes.string,
  isBatchGroup: PropTypes.bool,
  showLiveBatch: PropTypes.bool
};

Week.defaultProps = {
  showEducatorForLesson: false,
  isTestSeries: false,
  isSubscribed: false,
  forPlus: false,
  isBatch: false,
  cardPosition: -1,
  batch: null,
  subscription: {},
  renewalData: {},
  setLessonDrawer: doNothing,
  setAnchorEl: doNothing,
  showActionMenu: doNothing,
  showMoreMenu: false,
  goalUID: '',
  isBatchGroup: false,
  showLiveBatch: false
};
